import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import CustomToast from "../../components/misc/CustomToast";
import { useIsMobile } from "../../hooks/useIsMobile";
import { cn } from "../../utils/classMerger";
import FailedPostsList from "./FailedPostsList";
import RetrySettingsPanel from "./RetrySettingsPanel";
import { getFailedPosts, reschedulePosts } from "./actions";
import {
    FAILED_POST_PLATFORMS,
    FailedPostSortColumn,
    PaginatedFailedPosts,
    SortDirection,
} from "./types";

const FailedPosts: React.FC = () => {
    const mobile = useIsMobile(900);

    const [page, setPage] = useState(1);
    const [limit] = useState(10);
    const [data, setData] = useState<PaginatedFailedPosts>();
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("All");
    const [platformFilter, setPlatformFilter] = useState("All");
    const [fromDate, setFromDate] = useState<Date>(() => {
        const d = new Date();
        d.setMonth(d.getMonth() - 1);
        d.setHours(0, 0, 0, 0);
        return d;
    });
    const [toDate, setToDate] = useState<Date>(() => {
        const d = new Date();
        d.setHours(23, 59, 59, 999);
        return d;
    });
    const [sortBy, setSortBy] = useState<FailedPostSortColumn>();
    const [sortDirection, setSortDirection] = useState<SortDirection>("asc");

    // Debounce the owner-search input so typing doesn't fire a request per keystroke against the
    // cross-user failed-posts aggregation endpoint.
    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(searchTerm), 350);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    const fetchPosts = useCallback(
        async (p: number = page) => {
            setLoading(true);
            try {
                const result = await getFailedPosts({
                    page: p,
                    limit,
                    search: debouncedSearch,
                    status: statusFilter,
                    platform: platformFilter,
                    from: fromDate.toISOString(),
                    to: toDate.toISOString(),
                    sortBy,
                    sortDirection,
                });
                setData(result);
            } catch {
                toast.error(
                    <CustomToast
                        message="Failed to load failed posts"
                        type="error"
                    />,
                    { autoClose: 3000 }
                );
            } finally {
                setLoading(false);
            }
        },
        [
            page,
            limit,
            debouncedSearch,
            statusFilter,
            platformFilter,
            fromDate,
            toDate,
            sortBy,
            sortDirection,
        ]
    );

    useEffect(() => {
        fetchPosts(page);
    }, [page, fetchPosts]);

    const handleSortChange = (
        column: FailedPostSortColumn,
        direction: SortDirection
    ): void => {
        setSortBy(column);
        setSortDirection(direction);
    };

    const handleReschedule = async (
        postIds: string[],
        scheduledFor?: string
    ): Promise<void> => {
        try {
            const results = await reschedulePosts(postIds, scheduledFor);
            const failed = results.filter((r) => !r.ok);

            if (failed.length === 0) {
                toast.success(
                    <CustomToast
                        message={`Rescheduled ${results.length} post${
                            results.length > 1 ? "s" : ""
                        }.`}
                        type="success"
                    />,
                    { autoClose: 3000 }
                );
            } else {
                toast.warning(
                    <CustomToast
                        message={`${
                            results.length - failed.length
                        } rescheduled, ${failed.length} failed.`}
                        type="warning"
                    />,
                    { autoClose: 4000 }
                );
            }
            fetchPosts(page);
        } catch {
            toast.error(
                <CustomToast
                    message="Failed to reschedule posts. Please try again."
                    type="error"
                />,
                { autoClose: 3000 }
            );
        }
    };

    return (
        <div className={mobile ? "p-4" : "p-6"}>
            <div className={cn("grid items-start gap-8")}>
                <div className="grid gap-1 px-2">
                    <h1
                        className={cn(
                            "font-bold tracking-tight text-primary",
                            mobile ? "text-2xl" : "text-3xl"
                        )}
                    >
                        Failed Posts
                    </h1>
                    <p className="text-gray-500">
                        Reschedule posts that failed to publish, across all
                        users.
                    </p>
                </div>

                <RetrySettingsPanel fallbackTitle="Retry settings" />

                <FailedPostsList
                    posts={data?.items || []}
                    loading={loading}
                    page={page}
                    setPage={setPage}
                    limit={limit}
                    total={data?.total || 0}
                    searchTerm={searchTerm}
                    setSearchTerm={setSearchTerm}
                    statusFilter={statusFilter}
                    setStatusFilter={setStatusFilter}
                    platformFilter={platformFilter}
                    setPlatformFilter={setPlatformFilter}
                    platforms={FAILED_POST_PLATFORMS}
                    fromDate={fromDate}
                    toDate={toDate}
                    setFromDate={(d) => {
                        const next = new Date(d);
                        next.setHours(0, 0, 0, 0);
                        setFromDate(next);
                    }}
                    setToDate={(d) => {
                        const next = new Date(d);
                        next.setHours(23, 59, 59, 999);
                        setToDate(next);
                    }}
                    sortBy={sortBy}
                    sortDirection={sortDirection}
                    onSortChange={handleSortChange}
                    onReschedule={handleReschedule}
                    isMobile={mobile}
                />
            </div>
        </div>
    );
};

export default FailedPosts;
